import { z } from 'zod';
/**
 * THE FIVE APPLICATIONS, DECLARED ONCE. Visitor, Platform Admin, Employee Directory, Survey and
 * Mailroom — the suite `docs/002-architecture.md` lays out.
 *
 * This list is CODE and a closed set. It is the `<app>` segment of a permission
 * (`<app>.<resource>.<action>`, docs/005), the `<app>` prefix of a feature key
 * (`<app>.<feature>`, ./entitlement.js), the value of `AuditEvent.app`, and — since S38 — the
 * application a `Plan` sells. Adding one is a one-line change here plus the code that uses it;
 * it is never a migration and never an operator-editable row, which is CLAUDE.md's hardest-won
 * lesson stated for the fourth time.
 *
 * IT LIVES IN `contracts`, NOT `authz`, EVEN THOUGH `authz` IS ITS OLDEST CONSUMER. `authz`
 * depends on `contracts`; the reverse is a cycle. `@instanexus/authz` re-exports this list
 * unchanged as `PERMISSION_APPS`, so every existing call site keeps its name.
 *
 * ORDER IS PART OF THE CONTRACT: `platform` first, then the four product applications
 * alphabetically. Consumers iterate this tuple to emit a per-app answer in a stable order
 * (S38's `tenantEntitlementsDTOSchema.apps`), so reordering it reorders a wire payload.
 */
export const APP_IDS = ['platform', 'visitor', 'directory', 'survey', 'mailroom'];
/**
 * THE DISPLAY NAME OF EVERY APPLICATION, DECLARED ONCE. Every console, banner, dialog and
 * invoice line that names a product reads it from here, so a rename is a one-line change.
 *
 * Brand names are never translated (docs/011-i18n.md), so these are NOT i18n keys. A sentence
 * that mentions one passes it in as a parameter (`t('...', { app: PRODUCT_NAMES[id] })`).
 * `platform` is the suite itself (it sells no plan), `visitor` is the visitor product.
 */
export const PRODUCT_NAMES = {
    platform: 'InstaCheckin',
    visitor: 'InstaCheckin Visitor',
    directory: 'Employee Directory',
    survey: 'Surveys',
    mailroom: 'Mailroom',
};
/**
 * The one employee mobile app (adr/0037). Not an `AppId`: it sells nothing; it hosts modules.
 * A brand name, so never an i18n key; a sentence passes it in as `{ app }`.
 */
export const EMPLOYEE_APP_NAME = 'InstaCheckin Express';
/**
 * The iPad kiosk app, as listed on the App Store. Not an `AppId`: it is the Visitor product's
 * front-desk client. A brand name, so never an i18n key; a sentence passes it in as `{ app }`.
 * The kiosk's own Flutter, web and store files cannot import this: after changing it, run
 * `pnpm kiosk:sync-name` (scripts/sync-kiosk-app-name.mjs).
 */
export const KIOSK_APP_NAME = 'InstaCheckin Visitor Kiosk';
/** S4: the boundary validator. Every inbound `app` string is `safeParse`d through this. */
export const appIdSchema = z.enum(APP_IDS);
/**
 * Narrows a database `String` column (`Plan.app`, `AuditEvent.app`) to `AppId`. Returns false
 * for a value code no longer declares, which is the same rule `isFeatureKey` applies in
 * ./entitlement.js: the vocabulary is code and the database does not get to extend it.
 */
export function isAppId(value) {
    return APP_IDS.includes(value);
}
/**
 * The applications SOLD TO CUSTOMERS (owner decision 2026-09-26: launch with Visitor only).
 * CODE, like APP_IDS: launching an app is a one-line change here, shipped with that app's UI.
 * An app not listed keeps all of its per-app plumbing (plans, lines, trials, the wall), but it
 * is hidden from the customer price list, and checkout and add-an-app refuse it. `platform` is
 * listed because it is the suite itself — it has no plans to sell or hide. A deployment may sell more ahead of launch through the API's
 * BILLING_PRELAUNCH_APPS (never in prod) — see docs/019 "Which apps are sold".
 */
export const LAUNCHED_APPS = ['platform', 'visitor'];
/** Narrows an `AppId` to one this deployment's CODE sells, ignoring any per-environment
 *  pre-launch list (that is `isAppLaunched` in the API, `apps/api/src/modules/platform/billing/launch.ts`). */
export function isLaunchedApp(app) {
    return LAUNCHED_APPS.includes(app);
}
//# sourceMappingURL=app.js.map